import { z } from "zod";

export const QUESTION_TYPES = [
  "multiple_choice",
  "true_false",
  "written",
  "sort",
  "match",
  "fill_in_the_blank",
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

const choice = z.object({
  text: z.string().trim().min(1).max(1000),
  correct: z.boolean(),
});

/**
 * Per-type payload. Stored as JSON in `quiz_questions.content`; the prompt,
 * explanation, points and position stay as columns because they are common to
 * every type and get queried directly.
 */
export const multipleChoiceContent = z.object({
  options: z.array(choice).min(2).max(20),
  /** More than one correct option turns the question into a checkbox list. */
  multiple: z.boolean().default(false),
});

export const trueFalseContent = z.object({ correct: z.boolean() });

export const writtenContent = z.object({
  /** Reference answer. Never auto-graded — see `grade`. */
  answer: z.string().max(5000).default(""),
});

export const sortContent = z.object({
  options: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        text: z.string().trim().min(1).max(1000),
        /** Zero-based index in the correct sequence. */
        order: z.number().int().min(0),
      }),
    )
    .min(2)
    .max(20),
});

export const matchContent = z.object({
  pairs: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        left: z.string().trim().min(1).max(1000),
        right: z.string().trim().min(1).max(1000),
      }),
    )
    .min(2)
    .max(20),
});

export const fillInTheBlankContent = z.object({
  /** Blanks are `{{0}}`, `{{1}}`, … and index into `blanks`. */
  text: z.string().trim().min(1).max(5000),
  blanks: z
    .array(z.object({ options: z.array(choice).min(2).max(10) }))
    .min(1)
    .max(20),
});

export const questionContentByType = {
  multiple_choice: multipleChoiceContent,
  true_false: trueFalseContent,
  written: writtenContent,
  sort: sortContent,
  match: matchContent,
  fill_in_the_blank: fillInTheBlankContent,
} as const;

export type QuestionContent = {
  multiple_choice: z.infer<typeof multipleChoiceContent>;
  true_false: z.infer<typeof trueFalseContent>;
  written: z.infer<typeof writtenContent>;
  sort: z.infer<typeof sortContent>;
  match: z.infer<typeof matchContent>;
  fill_in_the_blank: z.infer<typeof fillInTheBlankContent>;
};

type QuestionOf<T extends QuestionType> = {
  id: string;
  type: T;
  prompt: string;
  explanation: string;
  points: number;
  position: number;
  content: QuestionContent[T];
};

/**
 * A question as the editor and the player both see it.
 *
 * Distributed over the type union rather than left generic, so `switch
 * (question.type)` narrows `question.content` on its own and graders need no
 * casts.
 */
export type Question = { [T in QuestionType]: QuestionOf<T> }[QuestionType];

export const questionInput = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("multiple_choice"),
    content: multipleChoiceContent,
  }),
  z.object({ type: z.literal("true_false"), content: trueFalseContent }),
  z.object({ type: z.literal("written"), content: writtenContent }),
  z.object({ type: z.literal("sort"), content: sortContent }),
  z.object({ type: z.literal("match"), content: matchContent }),
  z.object({
    type: z.literal("fill_in_the_blank"),
    content: fillInTheBlankContent,
  }),
]);

const questionCommon = z.object({
  prompt: z.string().trim().min(1).max(5000),
  explanation: z.string().max(5000).default(""),
  points: z.number().int().min(0).max(1000).default(1),
});

/** Full validator for a question coming from the admin editor. */
export const questionSchema = z.intersection(questionCommon, questionInput);

/**
 * Parses a `content` column. Returns null when the stored JSON does not match
 * the type — a question written by an older schema version, or hand-edited.
 */
export function parseContent<T extends QuestionType>(
  type: T,
  raw: string,
): QuestionContent[T] | null {
  try {
    const parsed = questionContentByType[type].safeParse(JSON.parse(raw));
    return parsed.success ? (parsed.data as QuestionContent[T]) : null;
  } catch {
    return null;
  }
}
